import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCourses } from '../context/CourseContext';
import { LogOut, User, BookOpen, Video, LayoutDashboard, Shield, LogIn, ChevronDown, Play, Menu, X } from 'lucide-react';

export const Navbar: React.FC = () => {
  const { user, profile, isAdmin, logout } = useAuth();
  const { subCategories, homepageSettings } = useCourses();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const mainCategories = [

    { id: 'school', name: 'স্কুল' },
    { id: 'academic', name: 'একাডেমিক' },
    { id: 'crc', name: 'CRC' }
  ];

  const defaultHeaderMenus = [
    { id: 'courses', title: 'কোর্সসমূহ', url: '/courses', isHidden: false },
    { id: 'blog', title: 'ব্লগ', url: '/blog', isHidden: false },
    { id: 'free-classes', title: 'ফ্রি ক্লাস', url: '/free-classes', isHidden: false }
  ];

  const headerMenus = homepageSettings?.headerMenus !== undefined && homepageSettings.headerMenus.length > 0
    ? homepageSettings.headerMenus
    : defaultHeaderMenus;

  // Filter out hidden menus strictly
  const visibleMenus = headerMenus.filter(menu => !menu.isHidden);

  const customPath = (homepageSettings?.adminCustomPath || 'ed-admin').trim().replace(/^\//, '');

  return (
    <nav className="bg-zinc-950 border-b border-zinc-900 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-2xl font-bold text-emerald-500 tracking-tighter">
                EduCore<span className="text-white">BD</span>
              </span>
            </Link>
            
            {/* Desktop Navigation Link Menus */}
            <div className="hidden lg:ml-8 lg:flex lg:space-x-4 items-center">
              {visibleMenus.map((menu) => {
                const isFreeLink = menu.url.includes('free') || menu.title.includes('ফ্রি');
                if (isFreeLink) {
                  return (
                    <Link
                      key={menu.id || menu.url}
                      to={menu.url}
                      className="bg-red-600/10 text-red-500 hover:bg-red-600 hover:text-white transition-all flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold border border-red-500/20"
                    >
                      <Play size={16} fill="currentColor" /> 
                      {menu.title}
                    </Link>
                  );
                }
                return (
                  <Link
                    key={menu.id || menu.url}
                    to={menu.url}
                    className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 px-2 text-sm font-medium"
                  >
                    <BookOpen size={16} /> 
                    {menu.title}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Desktop Controls Group */}
            <div className="hidden lg:flex items-center space-x-4">
              {user ? (
                <>
                  {isAdmin && (
                    <Link to={`/${customPath}`} className="text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-2 text-sm font-semibold">
                      <Shield size={18} /> এডমিন প্যানেল
                    </Link>
                  )}
                  <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm font-medium">
                    <LayoutDashboard size={18} /> Study
                  </Link>
                  <Link to="/profile" state={{ allowEdit: true }} className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm font-medium">
                    <User size={18} /> প্রোফাইল
                  </Link>
                  <div className="flex items-center gap-3 pl-4 border-l border-zinc-850">
                    <button 
                      onClick={() => logout()}
                      className="p-2 text-zinc-400 hover:text-red-400 transition-colors cursor-pointer"
                      title="Logout"
                    >
                      <LogOut size={18} />
                    </button>
                  </div>
                </>
              ) : (
                <Link
                  to="/login"
                  className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-sm"
                >
                  <LogIn size={18} /> লগইন করুন
                </Link>
              )}
            </div>

            {/* Smart Tablet-Only Quick Action Icons */}
            <div className="hidden md:flex lg:hidden items-center gap-2">
              {user ? (
                <>
                  {isAdmin && (
                    <Link 
                      to={`/${customPath}`} 
                      className="p-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-xl hover:bg-emerald-500 hover:text-white transition-all"
                      title="এডমিন প্যানেল"
                    >
                      <Shield size={16} />
                    </Link>
                  )}
                  <Link 
                    to="/dashboard" 
                    className="p-2 bg-zinc-900 text-zinc-400 border border-zinc-800 rounded-xl hover:text-white hover:bg-zinc-800 transition-all text-xs font-bold flex items-center gap-1.5"
                    title="Study Dashboard"
                  >
                    <LayoutDashboard size={16} />
                    <span className="hidden sm:inline">Study</span>
                  </Link>
                  <Link 
                    to="/profile" 
                    state={{ allowEdit: true }} 
                    className="w-9 h-9 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-emerald-500/50 flex items-center justify-center transition-all text-xs font-bold text-zinc-300 shadow-md"
                    title="প্রোফাইল"
                  >
                    {profile?.displayName ? (
                      <span className="text-emerald-400 font-extrabold">{profile.displayName.charAt(0).toUpperCase()}</span>
                    ) : (
                      <User size={15} className="text-zinc-450" />
                    )}
                  </Link>
                </>
              ) : (
                <Link
                  to="/login"
                  className="p-2 sm:px-3 sm:py-1.5 bg-emerald-600/10 text-emerald-400 hover:bg-emerald-600 hover:text-white rounded-xl font-bold transition-all flex items-center gap-1 text-xs border border-emerald-500/20"
                >
                  <LogIn size={14} /> <span>লগইন</span>
                </Link>
              )}

              {/* Tablet Toggle Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-xl transition-colors outline-none focus:outline-none cursor-pointer border border-transparent hover:border-zinc-800"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>

            {/* Mobile-Only Simple Toggle Button */}
            <div className="flex md:hidden items-center">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-xl transition-colors outline-none focus:outline-none cursor-pointer border border-transparent hover:border-zinc-800"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Smart, Tablet-Friendly Full Overlay Menu Panel (md:flex lg:hidden) */}
      {isMobileMenuOpen && (
        <div className="hidden md:flex lg:hidden fixed inset-x-0 top-16 bottom-0 bg-zinc-950/98 backdrop-blur-xl z-40 overflow-y-auto px-4 py-6 border-t border-zinc-900 flex-col space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
          
          {/* Section: Main Navigation */}
          <div>
            <span className="text-[10px] font-extrabold tracking-widest text-zinc-600 uppercase block mb-3 px-2">নেভিগেশন মেনু</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {visibleMenus.map((menu) => {
                const isFree = menu.url.includes('free') || menu.title.includes('ফ্রি');
                return (
                  <Link
                    key={menu.id || menu.url}
                    to={menu.url}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center justify-between p-4 rounded-2xl border transition-all duration-200 active:scale-98 ${
                      isFree 
                        ? 'bg-red-500/5 hover:bg-red-500/10 border-red-500/10 text-red-400 hover:border-red-500/30' 
                        : 'bg-zinc-900/50 hover:bg-zinc-900 border-zinc-800/80 text-zinc-300 hover:text-white hover:border-zinc-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-xl ${isFree ? 'bg-red-500/10 text-red-500' : 'bg-zinc-800 text-emerald-500'}`}>
                        {isFree ? <Play size={16} fill="currentColor" /> : <BookOpen size={16} />}
                      </div>
                      <div className="text-left">
                        <span className="text-sm font-bold block">{menu.title}</span>
                        <span className="text-[11px] text-zinc-500 block">
                          {isFree ? 'বিনামূল্যে শিক্ষণীয় কন্টেন্ট' : 'আমাদের সব কোর্স সমূহ'}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Section: Personal Hub & Account */}
          <div className="border-t border-zinc-900 pt-6">
            <span className="text-[10px] font-extrabold tracking-widest text-zinc-600 uppercase block mb-3 px-2">আপনার অ্যাকাউন্ট</span>
            
            {user ? (
              <div className="space-y-2">
                <div className="p-4 bg-zinc-900/40 border border-zinc-900 rounded-2xl flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-black">
                    {profile?.displayName ? profile.displayName.charAt(0).toUpperCase() : <User size={18} />}
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-bold text-white leading-tight">{profile?.displayName || 'ব্যবহারকারী'}</h4>
                    <p className="text-xs text-zinc-550">{user.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-3.5 bg-zinc-900/50 hover:bg-zinc-900 border border-zinc-800/80 hover:border-zinc-700 text-zinc-300 hover:text-white rounded-xl transition-all duration-150 text-sm font-bold active:scale-98"
                  >
                    <LayoutDashboard size={16} className="text-emerald-500" />
                    <span>Study Dashboard</span>
                  </Link>
                  
                  <Link
                    to="/profile"
                    state={{ allowEdit: true }}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-3.5 bg-zinc-900/50 hover:bg-zinc-900 border border-zinc-800/80 hover:border-zinc-700 text-zinc-300 hover:text-white rounded-xl transition-all duration-150 text-sm font-bold active:scale-98"
                  >
                    <User size={16} className="text-emerald-500" />
                    <span>প্রোফাইল সেটিংস</span>
                  </Link>

                  {isAdmin && (
                    <Link
                      to={`/${customPath}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 p-3.5 bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/10 hover:border-emerald-500/20 text-emerald-400 rounded-xl transition-all duration-150 text-sm font-bold active:scale-98 sm:col-span-2"
                    >
                      <Shield size={16} />
                      <span>এডমিন প্যানেল</span>
                    </Link>
                  )}

                  <button
                    onClick={() => {
                      logout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 p-3.5 bg-red-500/5 hover:bg-red-550/10 border border-red-500/10 hover:border-red-500/20 text-red-400 rounded-xl transition-all duration-150 text-sm font-bold cursor-pointer text-left active:scale-98 sm:col-span-2"
                  >
                    <LogOut size={16} />
                    <span>লগআউট করুন</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-2">
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white py-3.5 px-4 rounded-xl font-bold text-center block text-sm transition-all shadow-md active:scale-98"
                >
                  লগইন করুন
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile-Only Dropdown Panel (md:hidden) */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-zinc-950 border-t border-zinc-900/80 px-4 py-4 space-y-3 animate-in slide-in-from-top-3 duration-200">
          <div className="flex flex-col space-y-2 pb-3 border-b border-zinc-900">
            {visibleMenus.map((menu) => (
              <Link
                key={menu.id || menu.url}
                to={menu.url}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-zinc-300 hover:text-white transition-colors font-semibold flex items-center gap-2 py-2 px-3 hover:bg-zinc-900 rounded-xl"
              >
                <BookOpen size={16} className="text-emerald-500" />
                <span>{menu.title}</span>
              </Link>
            ))}
          </div>

          <div className="flex flex-col space-y-2 pt-1">
            {user ? (
              <>
                {isAdmin && (
                  <Link
                    to={`/${customPath}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-emerald-500 hover:text-white transition-colors font-bold flex items-center gap-2 py-2 px-3 hover:bg-emerald-950/10 rounded-xl"
                  >
                    <Shield size={16} />
                    <span>এডমিন প্যানেল</span>
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-zinc-300 hover:text-white transition-colors font-medium flex items-center gap-2 py-2 px-3 hover:bg-zinc-900 rounded-xl"
                >
                  <LayoutDashboard size={16} className="text-emerald-500" />
                  <span>Study Dashboard</span>
                </Link>
                <Link
                  to="/profile"
                  state={{ allowEdit: true }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-zinc-300 hover:text-white transition-colors font-medium flex items-center gap-2 py-2 px-3 hover:bg-zinc-900 rounded-xl"
                >
                  <User size={16} className="text-emerald-500" />
                  <span>প্রোফাইল</span>
                </Link>
                <button
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left text-red-400 hover:text-red-300 font-medium flex items-center gap-2 py-2 px-3 hover:bg-red-500/5 rounded-xl cursor-pointer"
                >
                  <LogOut size={16} />
                  <span>লগআউট করুন</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 px-4 rounded-xl font-bold text-center block text-sm"
              >
                লগইন করুন
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
